import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import OverallAnalytics from './components/OverallAnalytics';
import TrainingTypeAnalytics from './components/TrainingTypeAnalytics';
import CoverageMetrics from './components/CoverageMetrics';
import AssessmentMetrics from './components/AssessmentMetrics';
import PriorityAlerts from './components/PriorityAlerts';
import NavigationGrid from './components/NavigationGrid';

const TrainingComplianceDashboard = () => {
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [dateRange, setDateRange] = useState('30days');

  const overallMetrics = {
    completionRate: 87.5,
    completionTrend: 5.2,
    mandatoryCompliance: 92.3,
    mandatoryTrend: 3.1,
    overdueTraining: 23,
    overdueTrend: -12.5,
    certificationsExpiring: 15,
    certificationsTrend: 8.3,
    averageAssessmentScore: 84.2,
    assessmentTrend: 2.7,
    totalEmployees: 456,
    employeesTrend: 0
  };

  const trainingTypeData = {
    mandatory: {
      total: 245,
      completed: 226,
      inProgress: 12,
      overdue: 7,
      completionRate: 92.3,
      categories: [
        { name: 'Safety Orientation', completed: 456, total: 456, rate: 100 },
        { name: 'Fire Safety', completed: 442, total: 456, rate: 96.9 },
        { name: 'Hazardous Materials', completed: 398, total: 456, rate: 87.3 },
        { name: 'Emergency Response', completed: 421, total: 456, rate: 92.3 },
        { name: 'PPE Training', completed: 445, total: 456, rate: 97.6 }
      ]
    },
    skillBased: {
      total: 189,
      completed: 156,
      inProgress: 28,
      notStarted: 5,
      completionRate: 82.5,
      categories: [
        { name: 'Equipment Operation', completed: 89, total: 125, rate: 71.2 },
        { name: 'Process Safety', completed: 67, total: 89, rate: 75.3 },
        { name: 'Quality Control', completed: 112, total: 134, rate: 83.6 },
        { name: 'Maintenance Procedures', completed: 78, total: 98, rate: 79.6 },
        { name: 'Technical Certifications', completed: 45, total: 67, rate: 67.2 }
      ]
    }
  };

  const coverageData = {
    byDepartment: [
      { name: 'Production', total: 156, trained: 142, rate: 91.0, overdue: 8 },
      { name: 'Maintenance', total: 89, trained: 81, rate: 91.0, overdue: 5 },
      { name: 'Quality Control', total: 67, trained: 62, rate: 92.5, overdue: 3 },
      { name: 'Warehouse', total: 78, trained: 65, rate: 83.3, overdue: 4 },
      { name: 'Administration', total: 66, trained: 59, rate: 89.4, overdue: 3 }
    ],
    byLocation: [
      { name: 'Plant 1', total: 178, trained: 162, rate: 91.0, overdue: 9 },
      { name: 'Plant 2', total: 145, trained: 128, rate: 88.3, overdue: 8 },
      { name: 'Plant 3', total: 89, trained: 78, rate: 87.6, overdue: 4 },
      { name: 'Warehouse', total: 44, trained: 41, rate: 93.2, overdue: 2 }
    ],
    newHires: {
      total: 23,
      orientationComplete: 23,
      basicTrainingComplete: 18,
      advancedTrainingComplete: 12,
      avgCompletionTime: 14
    }
  };

  const assessmentData = {
    overallPassRate: 89.2,
    averageScore: 84.2,
    attemptsByCategory: [
      { name: 'Safety Orientation', attempts: 456, passed: 442, passRate: 96.9, avgScore: 88.5 },
      { name: 'Fire Safety', attempts: 442, passed: 398, passRate: 90.0, avgScore: 85.2 },
      { name: 'Hazardous Materials', attempts: 398, passed: 345, passRate: 86.7, avgScore: 82.1 },
      { name: 'Emergency Response', attempts: 421, passed: 387, passRate: 91.9, avgScore: 86.7 },
      { name: 'Equipment Operation', attempts: 89, passed: 78, passRate: 87.6, avgScore: 81.4 }
    ],
    retakeAnalysis: {
      firstAttemptPass: 78.4,
      secondAttemptPass: 18.9,
      thirdAttemptPass: 2.7,
      avgRetakes: 1.3
    },
    scoreDistribution: [
      { range: '90-100', count: 178, percentage: 39.0 },
      { range: '80-89', count: 145, percentage: 31.8 },
      { range: '70-79', count: 89, percentage: 19.5 },
      { range: '60-69', count: 34, percentage: 7.5 },
      { range: '<60', count: 10, percentage: 2.2 }
    ]
  };

  const priorityAlerts = [
    {
      id: 1,
      type: 'overdue',
      severity: 'high',
      title: 'Overdue Mandatory Training',
      description: '7 employees have overdue safety orientation training',
      count: 7,
      dueDate: '11/30/2025',
      action: 'Review & Reassign'
    },
    {
      id: 2,
      type: 'expiring',
      severity: 'medium',
      title: 'Certifications Expiring Soon',
      description: '15 forklift operator certifications expire within 30 days',
      count: 15,
      dueDate: '01/05/2026',
      action: 'Schedule Recertification'
    },
    {
      id: 3,
      type: 'assessment',
      severity: 'medium',
      title: 'Low Assessment Scores',
      description: '12 employees scored below 70% on hazmat assessment',
      count: 12,
      dueDate: 'Immediate',
      action: 'Schedule Retraining'
    },
    {
      id: 4,
      type: 'compliance',
      severity: 'high',
      title: 'Regulatory Compliance Gap',
      description: 'OSHA-required training pending for 5 new hires',
      count: 5,
      dueDate: '12/15/2025',
      action: 'Urgent Action Required'
    }
  ];

  const navigationItems = [
    {
      id: 'materials',
      title: 'Training Materials Repository',
      description: 'Manage training content, courses, and learning resources',
      icon: 'BookOpen',
      route: '/training-materials-repository',
      badge: '245 Resources',
      color: 'blue'
    },
    {
      id: 'assessments',
      title: 'Assessment Center',
      description: 'Create and manage training assessments and quizzes',
      icon: 'ClipboardCheck',
      route: '/training-assessments',
      badge: '89 Active',
      color: 'purple'
    },
    {
      id: 'certifications',
      title: 'Certification Management',
      description: 'Track employee certifications and renewals',
      icon: 'Award',
      route: '/training-certifications',
      badge: '156 Active',
      color: 'green'
    },
    {
      id: 'scheduler',
      title: 'Training Task Scheduler',
      description: 'Schedule upcoming training sessions and recertifications',
      icon: 'Calendar',
      route: '/training-scheduler',
      badge: '23 Upcoming',
      color: 'orange'
    }
  ];

  const locations = [
    { value: 'all', label: 'All Locations' },
    { value: 'plant-1', label: 'Plant 1 - Main Facility' },
    { value: 'plant-2', label: 'Plant 2 - North Wing' },
    { value: 'plant-3', label: 'Plant 3 - South Wing' },
    { value: 'warehouse-1', label: 'Warehouse 1' }
  ];

  const dateRanges = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: 'year', label: 'Last Year' }
  ];

  const handleNavigate = (route) => {
    navigate(route);
  };

  const handleExportReport = () => {
    console.log('Exporting training compliance report');
  };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Training & Compliance Dashboard</h1>
            <p className="text-muted-foreground">
              Comprehensive training program management with real-time analytics and compliance tracking
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportReport}
              className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="Download" size={20} />
              <span className="text-sm font-medium">Export Report</span>
            </button>
            <div className="flex items-center gap-2 px-4 py-2 bg-success/10 border border-success/20 rounded-lg">
              <Icon name="Shield" size={20} color="var(--color-success)" />
              <span className="text-sm font-medium text-success">Compliance Active</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="MapPin" size={18} color="var(--color-muted-foreground)" />
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e?.target?.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {locations?.map((location) => (
                <option key={location?.value} value={location?.value}>
                  {location?.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={18} color="var(--color-muted-foreground)" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {dateRanges?.map((range) => (
                <option key={range?.value} value={range?.value}>
                  {range?.label}
                </option>
              ))}
            </select>
          </div>
          <button className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
            <Icon name="RefreshCw" size={16} />
            Refresh Data
          </button>
        </div>
      </div>
      {/* Overall Analytics */}
      <OverallAnalytics metrics={overallMetrics} />
      {/* Training Type Analytics */}
      <TrainingTypeAnalytics data={trainingTypeData} />
      {/* Coverage and Assessment Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <CoverageMetrics data={coverageData} />
        <AssessmentMetrics data={assessmentData} />
      </div>
      {/* Priority Alerts */}
      <PriorityAlerts alerts={priorityAlerts} />
      {/* Navigation Grid */}
      <NavigationGrid items={navigationItems} onNavigate={handleNavigate} />
      {/* Integration Info */}
      <div className="bg-card border border-border rounded-lg p-6 mt-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Zap" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">HR System Integration Available</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Connect with your HR management system to automatically sync employee data, training assignments, and certification records.
            </p>
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
              <Icon name="Link" size={16} />
              Configure Integration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingComplianceDashboard;